import './ModalProducts.scss';

import * as formik from 'formik';
import * as yup from 'yup';

import { Col, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from 'react';

import AppButton from '../../commons/buttons/AppButton';
import AppCurrencyInput from '../../commons/inputs/AppCurrencyInput';
import AppInput from '../../commons/inputs/AppInput';
import AppLinkButton from '../../commons/buttons/AppLinkButton';
import AppLoading from '../../commons/AppLoading';
import AppTextAreaInput from '../../commons/inputs/AppTextAreaInput';
import HallAppToast from '../../commons/HallAppToast';
import ProductsAPI from './ProductsAPI';
import SelectBasicInput from '../../commons/inputs/SelectBasicInput';

const unitis = [
    { key: 'UN', label: 'Unidade (UN)' },
    { key: 'ML', label: 'Mililitros (ML)' },
    { key: 'L', label: 'Litros (L)' },
    { key: 'G', label: 'Gramas (g)' },
    { key: 'KG', label: 'Quilogramas (KG)' },
    { key: 'MG', label: 'Miligramas (MG)' },
    { key: 'CL', label: 'Centilitros (CL)' },
    { key: 'DL', label: 'Decilitros (DL)' },
    { key: 'CM3', label: 'Centímetros cúbicos (CM³)' },
    { key: 'M3', label: 'Metros cúbicos (M³)' },
    { key: 'PC', label: 'Peças (PC)' },
    { key: 'DZ', label: 'Dúzia (DZ)' }
];

const usageOptions = [
    { key: 'SALE', label: 'Venda' },
    { key: 'CONSUMPTION', label: 'Consumo' },
    { key: 'BOTH', label: 'Ambos' }
];

const priceDefinitionOptions = [
    { key: 'FIXED', label: 'Valor fixo (R$)' },
    { key: 'PERCENT', label: 'Percentual sobre custo (%)' }
];

const commissionTypeOptions = [
    { key: 'PERCENT', label: 'Percentual (%)' },
    { key: 'FIXED', label: 'Valor fixo (R$)' }
];

const productAPI = new ProductsAPI();

function ModalProduct({ closeShow, show, productToEdit, callGetProducts, companyId }) {
    const [isLoading, setIsLoading] = useState(false);

    const [initialValues, setInitialValues] = useState({
        name: '',
        unityType: 'UN',
        usageType: 'SALE',
        internalCode: '',
        barCode: '',
        line: '',
        category: '',
        mark: '',
        description: '',

        image: null,
        imagePreview: '',

        enablePricing: false,
        costPrice: '',

        customerSalePriceType: 'FIXED',
        customerSalePriceValue: '',

        employeeSalePriceType: 'FIXED',
        employeeSalePriceValue: '',

        employeeCommissionType: 'PERCENT',
        employeeCommissionValue: ''
    });

    const { Formik } = formik;

    const schema = yup.object().shape({
        name: yup.string()
            .required('Informe um nome para o produto')
            .max(150, 'Máximo de 150 caracteres'),

        unityType: yup.string().required('Informe a unidade'),
        usageType: yup.string().required('Informe o tipo de uso'),
        description: yup.string().nullable(),

        image: yup.mixed().nullable(),

        enablePricing: yup.boolean(),
        costPrice: yup.string(),

        customerSalePriceType: yup.string(),
        customerSalePriceValue: yup.string(),

        employeeSalePriceType: yup.string(),
        employeeSalePriceValue: yup.string(),

        employeeCommissionType: yup.string(),
        employeeCommissionValue: yup.string()
    });

    useEffect(() => {
        if (productToEdit && Object.keys(productToEdit).length > 0) {
            const imageUrl =
                productToEdit.imageUrl ||
                productToEdit.image ||
                productToEdit.photo ||
                '';

            setInitialValues({
                name: productToEdit.name || '',
                unityType: productToEdit.unityType || 'UN',
                usageType: productToEdit.usageType || 'SALE',
                internalCode: productToEdit.internalCode || '',
                barCode: productToEdit.barCode || '',
                line: productToEdit.line || '',
                category: productToEdit.category || '',
                mark: productToEdit.mark || '',
                description: productToEdit.description || '',

                image: null,
                imagePreview: imageUrl,

                enablePricing: !!(
                    productToEdit.pricing?.costPrice ||
                    productToEdit.pricing?.customerSalePrice ||
                    productToEdit.pricing?.employeeSalePrice ||
                    productToEdit.pricing?.employeeCommissionValue
                ),

                costPrice: productToEdit.pricing?.costPrice || '',

                customerSalePriceType: productToEdit.pricing?.customerSalePriceType || 'FIXED',
                customerSalePriceValue: productToEdit.pricing?.customerSalePriceValue || productToEdit.pricing?.customerSalePrice || '',

                employeeSalePriceType: productToEdit.pricing?.employeeSalePriceType || 'FIXED',
                employeeSalePriceValue: productToEdit.pricing?.employeeSalePriceValue || productToEdit.pricing?.employeeSalePrice || '',

                employeeCommissionType: productToEdit.pricing?.employeeCommissionType || 'PERCENT',
                employeeCommissionValue: productToEdit.pricing?.employeeCommissionValue || ''
            });
        } else {
            setInitialValues({
                name: '',
                unityType: 'UN',
                usageType: 'SALE',
                internalCode: '',
                barCode: '',
                line: '',
                category: '',
                mark: '',
                description: '',

                image: null,
                imagePreview: '',

                enablePricing: false,
                costPrice: '',

                customerSalePriceType: 'FIXED',
                customerSalePriceValue: '',

                employeeSalePriceType: 'FIXED',
                employeeSalePriceValue: '',

                employeeCommissionType: 'PERCENT',
                employeeCommissionValue: ''
            });
        }
    }, [productToEdit, show]);

    const toNumber = (v) => {
        if (typeof v === 'number') return v;
        if (v == null || v === '') return 0;

        const normalized = String(v)
            .replace(/\s/g, '')
            .replace(/\./g, '')
            .replace(',', '.')
            .replace(/[^\d.-]/g, '');

        return Number(normalized) || 0;
    };

    const formatMoneyLabel = (value) => {
        if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
            return 'R$ 0,00';
        }

        return Number(value).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const calculateSalePriceFromType = (costPrice, type, value) => {
        const cost = toNumber(costPrice);
        const ruleValue = toNumber(value);

        if (!ruleValue) return '';

        if (type === 'PERCENT') {
            if (!cost) return '';
            return Number((cost * (1 + (ruleValue / 100))).toFixed(2));
        }

        return Number(ruleValue.toFixed(2));
    };

    const calculateCommissionPreview = (commissionType, commissionValue, salePrice) => {
        const price = toNumber(salePrice);
        const value = toNumber(commissionValue);

        if (!value) return '';
        if (commissionType === 'PERCENT') {
            if (!price) return '';
            return Number((price * (value / 100)).toFixed(2));
        }

        return Number(value.toFixed(2));
    };

    const priceRuleLabel = (type, value) => {
        if (value === '' || value === null || value === undefined) return 'Não definido';

        if (type === 'PERCENT') {
            return `${Number(value)}% sobre o custo`;
        }

        return formatMoneyLabel(value);
    };

    const commissionRuleLabel = (type, value) => {
        if (value === '' || value === null || value === undefined) return 'Não definida';

        if (type === 'PERCENT') {
            return `${Number(value)}% por venda`;
        }

        return `${formatMoneyLabel(value)} por venda`;
    };

    const buildPayload = (values) => {
        const costPrice = values.enablePricing && values.costPrice !== ''
            ? toNumber(values.costPrice)
            : null;

        const customerSalePrice = values.enablePricing && values.customerSalePriceValue !== ''
            ? calculateSalePriceFromType(
                values.costPrice,
                values.customerSalePriceType,
                values.customerSalePriceValue
            )
            : null;

        const employeeSalePrice = values.enablePricing && values.employeeSalePriceValue !== ''
            ? calculateSalePriceFromType(
                values.costPrice,
                values.employeeSalePriceType,
                values.employeeSalePriceValue
            )
            : null;

        return {
            name: values.name,
            unityType: values.unityType,
            usageType: values.usageType,
            internalCode: values.internalCode || null,
            barCode: values.barCode || null,
            line: values.line || null,
            category: values.category || null,
            mark: values.mark || null,
            description: values.description || null,

            pricing: values.enablePricing ? {
                costPrice,
                customerSalePriceType: values.customerSalePriceType || null,
                customerSalePriceValue: values.customerSalePriceValue !== ''
                    ? toNumber(values.customerSalePriceValue)
                    : null,
                customerSalePrice,

                employeeSalePriceType: values.employeeSalePriceType || null,
                employeeSalePriceValue: values.employeeSalePriceValue !== ''
                    ? toNumber(values.employeeSalePriceValue)
                    : null,
                employeeSalePrice,

                employeeCommissionType: values.employeeCommissionType || null,
                employeeCommissionValue: values.employeeCommissionValue !== ''
                    ? toNumber(values.employeeCommissionValue)
                    : null
            } : null
        };
    };

    const buildFormData = (payload, imageFile) => {
        const formData = new FormData();

        formData.append(
            'product',
            new Blob([JSON.stringify(payload)], { type: 'application/json' })
        );

        if (imageFile instanceof File) {
            formData.append('image', imageFile);
        }

        return formData;
    };

    const save = async (values) => {
        setIsLoading(true);

        try {
            const data = buildPayload(values);
            const hasNewImage = values.image instanceof File;

            if (productToEdit?.id) {
                if (hasNewImage) {
                    const formData = buildFormData(data, values.image);
                    await productAPI.updateFormData(productToEdit.id, formData, companyId);
                } else {
                    await productAPI.update(productToEdit.id, data, companyId);
                }
            } else {
                if (hasNewImage) {
                    const formData = buildFormData(data, values.image);
                    await productAPI.createFormData(formData, companyId);
                } else {
                    await productAPI.create(data, companyId);
                }
            }

            HallAppToast.success();

            if (callGetProducts) await callGetProducts();

            closeShow();
        } catch (_) {
            HallAppToast.genericError();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} fullscreen={true} onHide={closeShow}>
            <Modal.Header className='custom-close-button' closeButton>
                <Modal.Title style={{ textAlign: 'center', fontSize: '15px' }}>
                    {productToEdit?.id ? 'EDITAR PRODUTO' : 'NOVO PRODUTO'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={save}
                    initialValues={initialValues}
                    enableReinitialize
                >
                    {({ handleSubmit, handleChange, values, touched, errors, setFieldValue }) => {
                        const customerSalePricePreview = calculateSalePriceFromType(
                            values.costPrice,
                            values.customerSalePriceType,
                            values.customerSalePriceValue
                        );

                        const employeeSalePricePreview = calculateSalePriceFromType(
                            values.costPrice,
                            values.employeeSalePriceType,
                            values.employeeSalePriceValue
                        );

                        const employeeCommissionPreviewOnCustomerSale = calculateCommissionPreview(
                            values.employeeCommissionType,
                            values.employeeCommissionValue,
                            customerSalePricePreview
                        );

                        const employeeCommissionPreviewOnEmployeeSale = calculateCommissionPreview(
                            values.employeeCommissionType,
                            values.employeeCommissionValue,
                            employeeSalePricePreview
                        );

                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <div className='container'>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                isRequired
                                                name="name"
                                                onChange={handleChange}
                                                value={values.name}
                                                label="Nome"
                                            />
                                        </Col>

                                        <Col xs={12} md={3}>
                                            <SelectBasicInput
                                                errors={errors}
                                                touched={touched}
                                                name='unityType'
                                                onChange={handleChange}
                                                value={values.unityType}
                                                options={unitis}
                                                label='Unidade'
                                            />
                                        </Col>

                                        <Col xs={12} md={3}>
                                            <SelectBasicInput
                                                errors={errors}
                                                touched={touched}
                                                name='usageType'
                                                onChange={handleChange}
                                                value={values.usageType}
                                                options={usageOptions}
                                                label='Uso do Produto'
                                            />
                                        </Col>
                                    </Row>

                                    <div className="product-image-upload-wrapper">
                                        <label className="product-image-label">Foto do produto</label>

                                        <div className="product-image-card">
                                            <div className="product-image-preview-container">
                                                <div className="product-image-preview-box">
                                                    {values.imagePreview ? (
                                                        <img
                                                            src={values.imagePreview}
                                                            alt="Produto"
                                                            className="product-image-preview"
                                                        />
                                                    ) : (
                                                        <div className="product-image-placeholder">
                                                            <div className="product-image-placeholder-icon">📷</div>
                                                            <span>Selecione uma imagem para o produto</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="product-image-input"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] || null;
                                                    setFieldValue('image', file);

                                                    if (file) {
                                                        setFieldValue('imagePreview', URL.createObjectURL(file));
                                                    }
                                                }}
                                            />

                                            <div className="product-image-help">
                                                JPG, PNG ou WEBP. Ao selecionar uma imagem, o produto será salvo com multipart/form-data.
                                            </div>

                                            {values.imagePreview && (
                                                <div className="product-image-actions">
                                                    <button
                                                        type="button"
                                                        className="product-image-remove-btn"
                                                        onClick={() => {
                                                            setFieldValue('image', null);
                                                            setFieldValue('imagePreview', '');
                                                        }}
                                                    >
                                                        Remover imagem
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Row>
                                        <Col xs={12} md={6}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                name="internalCode"
                                                onChange={handleChange}
                                                value={values.internalCode}
                                                label="Código interno"
                                            />
                                        </Col>

                                        <Col xs={12} md={6}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                name='barCode'
                                                onChange={handleChange}
                                                value={values.barCode}
                                                label='Código de barras'
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={12} md={4}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                name="line"
                                                onChange={handleChange}
                                                value={values.line}
                                                label="Linha"
                                            />
                                        </Col>

                                        <Col xs={12} md={4}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                name='category'
                                                onChange={handleChange}
                                                value={values.category}
                                                label='Categoria'
                                            />
                                        </Col>

                                        <Col xs={12} md={4}>
                                            <AppInput
                                                errors={errors}
                                                touched={touched}
                                                name='mark'
                                                onChange={handleChange}
                                                value={values.mark}
                                                label='Marca'
                                            />
                                        </Col>
                                    </Row>

                                    <AppTextAreaInput
                                        errors={errors}
                                        touched={touched}
                                        name='description'
                                        onChange={handleChange}
                                        value={values.description}
                                        label='Descrição'
                                    />

                                    <hr />

                                    <Row>
                                        <Col xs={12}>
                                            <Form.Check
                                                id="enablePricing"
                                                type="switch"
                                                label="Definir também os preços e comissão do produto"
                                                checked={!!values.enablePricing}
                                                onChange={(e) => setFieldValue('enablePricing', e.target.checked)}
                                            />
                                        </Col>
                                    </Row>

                                    {values.enablePricing && (
                                        <>
                                            <div style={{ marginTop: 16, marginBottom: 8 }}>
                                                <h6 style={{ fontWeight: 700, color: '#2f2a7a' }}>
                                                    Regras de preço
                                                </h6>
                                            </div>

                                            <Row>
                                                <Col xs={12} md={4}>
                                                    <AppCurrencyInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name="costPrice"
                                                        value={values.costPrice}
                                                        type="text"
                                                        label='Custo de compra'
                                                        onChange={(_, value) => {
                                                            setFieldValue('costPrice', value);
                                                        }}
                                                    />
                                                </Col>

                                                <Col xs={12} md={4}>
                                                    <SelectBasicInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name='customerSalePriceType'
                                                        onChange={(e) => {
                                                            setFieldValue('customerSalePriceType', e.target.value);
                                                            setFieldValue('customerSalePriceValue', '');
                                                        }}
                                                        value={values.customerSalePriceType}
                                                        options={priceDefinitionOptions}
                                                        label='Preço para cliente'
                                                    />
                                                </Col>

                                                <Col xs={12} md={4}>
                                                    {values.customerSalePriceType === 'FIXED' ? (
                                                        <AppCurrencyInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="customerSalePriceValue"
                                                            value={values.customerSalePriceValue}
                                                            type="text"
                                                            label='Valor para cliente'
                                                            onChange={(_, value) => {
                                                                setFieldValue('customerSalePriceValue', value);
                                                            }}
                                                            formText={
                                                                customerSalePricePreview
                                                                    ? `Preço final: ${formatMoneyLabel(customerSalePricePreview)}`
                                                                    : undefined
                                                            }
                                                        />
                                                    ) : (
                                                        <AppInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="customerSalePriceValue"
                                                            onChange={handleChange}
                                                            value={values.customerSalePriceValue}
                                                            type="number"
                                                            label='% sobre custo para cliente'
                                                            formText={
                                                                customerSalePricePreview
                                                                    ? `Preço final: ${formatMoneyLabel(customerSalePricePreview)}`
                                                                    : 'Ex: 100 = dobra o custo'
                                                            }
                                                        />
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12} md={4}>
                                                    <SelectBasicInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name='employeeSalePriceType'
                                                        onChange={(e) => {
                                                            setFieldValue('employeeSalePriceType', e.target.value);
                                                            setFieldValue('employeeSalePriceValue', '');
                                                        }}
                                                        value={values.employeeSalePriceType}
                                                        options={priceDefinitionOptions}
                                                        label='Preço para profissional'
                                                    />
                                                </Col>

                                                <Col xs={12} md={4}>
                                                    {values.employeeSalePriceType === 'FIXED' ? (
                                                        <AppCurrencyInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="employeeSalePriceValue"
                                                            value={values.employeeSalePriceValue}
                                                            type="text"
                                                            label='Valor para profissional'
                                                            onChange={(_, value) => {
                                                                setFieldValue('employeeSalePriceValue', value);
                                                            }}
                                                            formText={
                                                                employeeSalePricePreview
                                                                    ? `Preço final: ${formatMoneyLabel(employeeSalePricePreview)}`
                                                                    : undefined
                                                            }
                                                        />
                                                    ) : (
                                                        <AppInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="employeeSalePriceValue"
                                                            onChange={handleChange}
                                                            value={values.employeeSalePriceValue}
                                                            type="number"
                                                            label='% sobre custo para profissional'
                                                            formText={
                                                                employeeSalePricePreview
                                                                    ? `Preço final: ${formatMoneyLabel(employeeSalePricePreview)}`
                                                                    : 'Ex: 80 = custo + 80%'
                                                            }
                                                        />
                                                    )}
                                                </Col>

                                                <Col xs={12} md={4}>
                                                    <SelectBasicInput
                                                        errors={errors}
                                                        touched={touched}
                                                        name='employeeCommissionType'
                                                        onChange={handleChange}
                                                        value={values.employeeCommissionType}
                                                        options={commissionTypeOptions}
                                                        label='Tipo de comissão'
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12} md={4}>
                                                    {values.employeeCommissionType === 'FIXED' ? (
                                                        <AppCurrencyInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="employeeCommissionValue"
                                                            value={values.employeeCommissionValue}
                                                            type="text"
                                                            label='Comissão'
                                                            onChange={(_, value) => {
                                                                setFieldValue('employeeCommissionValue', value);
                                                            }}
                                                        />
                                                    ) : (
                                                        <AppInput
                                                            errors={errors}
                                                            touched={touched}
                                                            name="employeeCommissionValue"
                                                            onChange={handleChange}
                                                            value={values.employeeCommissionValue}
                                                            type="number"
                                                            label='Comissão (%)'
                                                        />
                                                    )}
                                                </Col>
                                            </Row>

                                            <div
                                                style={{
                                                    marginTop: 20,
                                                    border: '1px solid #e6e1ff',
                                                    background: '#f8f6ff',
                                                    borderRadius: 16,
                                                    padding: 16
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 700,
                                                        color: '#2f2a7a',
                                                        fontSize: 15,
                                                        marginBottom: 12
                                                    }}
                                                >
                                                    Resumo da venda
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 10,
                                                        fontSize: 14
                                                    }}
                                                >
                                                    <div>
                                                        <strong>Custo do produto:</strong>{' '}
                                                        {values.costPrice
                                                            ? formatMoneyLabel(values.costPrice)
                                                            : 'Não informado'}
                                                    </div>

                                                    <div>
                                                        <strong>Venda para cliente:</strong>{' '}
                                                        {priceRuleLabel(
                                                            values.customerSalePriceType,
                                                            values.customerSalePriceValue
                                                        )}
                                                        {customerSalePricePreview ? (
                                                            <> • preço final {formatMoneyLabel(customerSalePricePreview)}</>
                                                        ) : null}
                                                    </div>

                                                    <div>
                                                        <strong>Venda para profissional:</strong>{' '}
                                                        {priceRuleLabel(
                                                            values.employeeSalePriceType,
                                                            values.employeeSalePriceValue
                                                        )}
                                                        {employeeSalePricePreview ? (
                                                            <> • preço final {formatMoneyLabel(employeeSalePricePreview)}</>
                                                        ) : null}
                                                    </div>

                                                    <div>
                                                        <strong>Comissão:</strong>{' '}
                                                        {commissionRuleLabel(
                                                            values.employeeCommissionType,
                                                            values.employeeCommissionValue
                                                        )}
                                                    </div>

                                                    {(customerSalePricePreview || employeeSalePricePreview) && (
                                                        <div
                                                            style={{
                                                                marginTop: 4,
                                                                paddingTop: 12,
                                                                borderTop: '1px dashed #d9cffd',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 8
                                                            }}
                                                        >
                                                            {customerSalePricePreview ? (
                                                                <div>
                                                                    <strong>Exemplo na venda para cliente:</strong>{' '}
                                                                    vende por {formatMoneyLabel(customerSalePricePreview)}
                                                                    {employeeCommissionPreviewOnCustomerSale ? (
                                                                        <>
                                                                            {' '}• comissão do profissional{' '}
                                                                            {formatMoneyLabel(employeeCommissionPreviewOnCustomerSale)}
                                                                        </>
                                                                    ) : null}
                                                                </div>
                                                            ) : null}

                                                            {employeeSalePricePreview ? (
                                                                <div>
                                                                    <strong>Exemplo na venda para profissional:</strong>{' '}
                                                                    vende por {formatMoneyLabel(employeeSalePricePreview)}
                                                                    {employeeCommissionPreviewOnEmployeeSale ? (
                                                                        <>
                                                                            {' '}• comissão do profissional{' '}
                                                                            {formatMoneyLabel(employeeCommissionPreviewOnEmployeeSale)}
                                                                        </>
                                                                    ) : null}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <hr />

                                    {isLoading ? (
                                        <div style={{ textAlign: 'center' }}>
                                            <AppLoading />
                                        </div>
                                    ) : (
                                        <AppButton type="submit" width='100%' name='salvar' />
                                    )}

                                    <div style={{ marginTop: '10px' }}>
                                        <AppLinkButton name="fechar" onClick={closeShow} />
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal.Body>
        </Modal>
    );
}

export default ModalProduct;